import {
  Body,
  Col,
  Flex,
  Input,
  Loading,
  Modal,
  ModalBody,
  ModalHeader,
  Pagination,
  Row,
  Shape,
  SubTitle,
  Title,
  styled,
} from '@teko/ds-desktop';
import { SearchOutlined } from '@teko/ds-icon';
import { SelectionOption, VerticalFilter } from '@teko/teko-ecommerce';
import { SizeEnum } from '@teko/types/ds';
import { Filter, FilterOption, ProductCardProps } from '@teko/types/ecommerce';
import { cleanObject } from '@teko/utils';
import {
  BUILD_PC_MODAL_PAGE_SIZE,
  DEFAULT_PRICE_FILTER,
  ITEM_PER_PAGE_IN_LISTING_PAGE,
} from 'constants/index';
import {
  convertDiscoveryFilter,
  convertFilter,
  convertToPayloadSorting,
  getDisplayFilter,
} from 'helpers';
import { useBuildPcProducts, usePos365StoreCode, useTenantConfig } from 'hooks';
import { t } from 'i18n';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState } from 'react';
import {
  SortOptionInterface,
  cookFilterPrice,
  extractSortQuery,
  getDefaultSortOptions,
} from '../../../../modules/search-page/shared';
import { HamperGrid } from '../CustomHamper/hamperComponent/HamperGrid';

const ProductList = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.ProductList),
);

const ProductGrid = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.ProductGrid),
);

const NoProductsFound = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.NoProductsFound),
);

interface ProductSelectModalComponentProps {
  visible: boolean;
  onClose: () => void;
  listProducts: ProductCardProps[];
  pagination: {
    totalItems: number;
    totalPages: number;
  };
  filterPrice: {
    max: number;
    min: number;
    step: number;
    value: {
      gte: number;
      lte: number;
    };
  };
  currentPage: number;
  isLoading?: boolean;
  onPageChange: (page: number) => void;
  title?: string;
  filters: Filter[];
  activeOptions: FilterOption[];
  activeSortOption?: SortOptionInterface;
  onSortOptionSelected: (sortOption: SortOptionInterface) => void;
  onClickFilterOption: (option: FilterOption) => void;
  onClearFilter: () => void;
  onSortByPrice: (priceGte: number, priceLte: number) => void;
  onClickSearch: (keyword: string) => void;
  isUsingHamper?: boolean;
  hamperItemKey?: string;
}

interface BuildPcModalProps {
  collectionSlug: string;
  categoryName: string;
  visible: boolean;
  onClose: () => void;
}

export const ProductSelectModalComponent: React.FC<
  ProductSelectModalComponentProps
> = (props) => {
  const {
    listProducts,
    pagination,
    filters,
    activeOptions,
    filterPrice,
    activeSortOption,
    visible,
    onPageChange,
    onClose,
    onSortOptionSelected,
    onClickFilterOption,
    onClearFilter,
    onSortByPrice,
    onClickSearch,
    currentPage,
    isLoading = false,
    hamperItemKey = '',
    isUsingHamper = false,
    title = t('Chọn linh kiện'),
  } = props;
  const { totalItems, totalPages } = pagination;
  const { pos365StoreCode } = usePos365StoreCode();
  const { sortByCreatedAt } = useTenantConfig();

  return (
    <StyledModal
      visible={visible}
      onClose={onClose}
      width="90%"
      contentStyle={{ height: '85vh', padding: 0 }}
      okText=""
      cancelText=""
      footer={null}
    >
      <ModalHeader
        style={{
          borderBottom: '1px solid #E4E5F0',
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 12,
          paddingBottom: 12,
          height: '10%',
        }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col span={2} style={{ textAlign: 'left' }}>
            <StyledTitle paddingLeft="s8">{title}</StyledTitle>
          </Col>
          <Col span={10} style={{ paddingLeft: 0 }}>
            <Input
              placeholder={t('Nhập từ khóa cần tìm')}
              onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>): void => {
                const keyword = (e.target as HTMLInputElement).value;
                if (e.keyCode === 13) {
                  onClickSearch && onClickSearch(keyword);
                }
              }}
              suffix={<SearchOutlined size={20} />}
              width="60%"
            />
          </Col>
        </Row>
      </ModalHeader>
      <ModalBody style={{ height: '90%' }} padding="s0">
        <Row gutter={[16, 16]} height="100%">
          <Col
            span={2}
            height="inherit"
            style={{ overflow: 'hidden scroll', scrollbarWidth: 'none' }}
            borderRight="1px solid divider"
          >
            <VerticalFilter
              activeOptions={activeOptions}
              filters={filters}
              onClickOption={onClickFilterOption}
              onClearFilter={onClearFilter}
              filterPrice={
                filterPrice
                  ? {
                      max: filterPrice.max,
                      min: filterPrice.min,
                      step: filterPrice.step,
                      value: {
                        gte: filterPrice.value.gte,
                        lte: filterPrice.value.lte,
                      },
                      onFilter: onSortByPrice,
                    }
                  : undefined
              }
            />
          </Col>
          <Col span={10} style={{ paddingLeft: 0, height: '100%' }}>
            <Flex width="100%" borderBottom="1px solid divider" align="center">
              <Flex
                justify="start"
                align="center"
                paddingLeft="s16"
                paddingBottom="s8"
                paddingTop="s8"
                width="100%"
              >
                <SubTitle style={{ lineHeight: '14px' }} marginRight="s32">
                  {t('Sắp xếp theo')}
                </SubTitle>
                <HorizontalScrollContainer>
                  {getDefaultSortOptions(sortByCreatedAt).map(
                    (option, index: number) => (
                      <SelectionOption
                        key={index}
                        iconActiveSize={SizeEnum.s}
                        block={false}
                        isActive={
                          activeSortOption?.optionCode === option.optionCode
                        }
                        onClick={() => onSortOptionSelected(option)}
                        style={{
                          marginRight: '1rem',
                        }}
                      >
                        <Body level={1}>{t(option.optionName)}</Body>
                      </SelectionOption>
                    ),
                  )}
                </HorizontalScrollContainer>
              </Flex>
            </Flex>
            {!listProducts.length && !isLoading ? (
              <NoProductsFound show />
            ) : (
              <div style={{ height: '90%' }}>
                <StyledList
                  style={{
                    overflow: 'hidden scroll',
                  }}
                >
                  {!!pos365StoreCode ? (
                    <StyledProductList
                      products={listProducts}
                      regionName="itemProductResult"
                      itemsPerPage={
                        Math.ceil(totalItems / totalPages) ||
                        ITEM_PER_PAGE_IN_LISTING_PAGE
                      }
                    />
                  ) : isUsingHamper ? (
                    <StyledHamperGrid
                      products={listProducts}
                      regionName="itemProductResult"
                      itemsPerPage={
                        Math.ceil(totalItems / totalPages) ||
                        ITEM_PER_PAGE_IN_LISTING_PAGE
                      }
                      hamperItemKey={hamperItemKey}
                    />
                  ) : (
                    <StyledProductGrid
                      products={listProducts}
                      regionName="itemProductResult"
                      itemsPerPage={
                        Math.ceil(totalItems / totalPages) ||
                        ITEM_PER_PAGE_IN_LISTING_PAGE
                      }
                    />
                  )}
                </StyledList>
                <Shape
                  style={{ height: '5%' }}
                  padding="s8"
                  borderTop="1px solid divider"
                >
                  <Pagination
                    align="center"
                    totalRecords={totalItems}
                    currentPage={currentPage}
                    onPageChange={onPageChange}
                  />
                </Shape>
              </div>
            )}
          </Col>
        </Row>
      </ModalBody>
    </StyledModal>
  );
};

const StyledTitle = styled(Title)`
  line-height: 24px;
  font-size: 18px;
  @media (min-width: 1393px) {
    line-height: 24px;
    font-size: 20px;
  }
`;
const StyledList = styled(Shape)`
  height: 85%;
  @media (min-width: 1393px) {
    height: 90%;
  }
`;

const HorizontalScrollContainer = styled.div`
  width: 100%;
  overflow-x: scroll;
  white-space: nowrap;
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

export const BuildPcModal: React.FC<BuildPcModalProps> = (props) => {
  const { visible, onClose, collectionSlug, categoryName } = props;
  const [priceFilterValue, setPriceFilterValue] = useState<{
    gte: number;
    lte: number;
  }>();
  const [keyword, setKeyword] = useState('');
  const [activeOptions, setActiveOptions] = useState<FilterOption[]>([]);
  const [activeSortOption, setActiveSortOption] = useState<
    SortOptionInterface | undefined
  >();
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [keyword]);

  const [sort, order] = activeSortOption
    ? extractSortQuery(activeSortOption.optionCode)
    : [undefined, undefined];

  // fetch filter block data
  const { filter, loading: filterLoading } = useBuildPcProducts(
    {
      slug: collectionSlug,
      returnFilterable: [
        'FILTER_TYPE_ATTRIBUTE',
        'FILTER_TYPE_BRAND',
        'FILTER_TYPE_PRICE',
        'FILTER_TYPE_CLEARANCE',
      ],
      pageSize: 1,
    },
    onClose,
    categoryName,
  );

  // fetch product list
  const {
    products,
    pagination,
    loading: productLoading,
  } = useBuildPcProducts(
    {
      slug: collectionSlug,
      query: keyword,
      sorting: convertToPayloadSorting(sort, order),
      filter: cleanObject({
        ...convertFilter(activeOptions),
        priceGte: priceFilterValue?.gte,
        priceLte: priceFilterValue?.lte,
      }),
      pageSize: BUILD_PC_MODAL_PAGE_SIZE,
      page: currentPage,
    },
    onClose,
    categoryName,
  );

  const filters = useMemo(() => {
    return !!filter ? convertDiscoveryFilter(filter) : [];
  }, [filter]);
  const displayFilters = useMemo(() => {
    return getDisplayFilter(filters);
  }, [filters]);
  const priceFilter = useMemo(() => {
    return cookFilterPrice(filter?.priceLte, filter?.priceGte);
  }, [filter?.priceLte, filter?.priceGte]);

  const onSortOptionSelected = (sortOption: SortOptionInterface) => {
    if (sortOption.optionCode === activeSortOption?.optionCode) {
      setActiveSortOption(undefined);
    } else {
      setActiveSortOption(sortOption);
    }
  };

  const onClickFilterOption = (option: FilterOption) => {
    const optionIndex = activeOptions.findIndex(
      (opt) =>
        opt.filterCode === option.filterCode &&
        opt.filterId === option.filterId &&
        opt.optionId === option.optionId,
    );
    if (optionIndex > -1) {
      setActiveOptions((prev) =>
        prev.filter((_, index) => index !== optionIndex),
      );
    } else {
      setActiveOptions((prev) => [...prev, option]);
    }
  };

  const onClickSearch = (keyword: string) => {
    setKeyword(keyword);
  };

  return (
    <Loading isLoading={filterLoading || productLoading}>
      <ProductSelectModalComponent
        visible={visible}
        onClose={onClose}
        listProducts={products}
        filters={displayFilters}
        pagination={pagination || { totalItems: 0, totalPages: 0 }}
        filterPrice={{
          ...priceFilter,
          value: priceFilterValue || DEFAULT_PRICE_FILTER,
        }}
        currentPage={currentPage}
        onSortByPrice={(gte: number, lte: number) =>
          setPriceFilterValue({ gte, lte })
        }
        onPageChange={(page) => setCurrentPage(page)}
        activeSortOption={activeSortOption}
        activeOptions={activeOptions}
        onSortOptionSelected={onSortOptionSelected}
        onClearFilter={() => setActiveOptions([])}
        onClickFilterOption={onClickFilterOption}
        onClickSearch={onClickSearch}
        isLoading={filterLoading || productLoading}
      />
    </Loading>
  );
};

const StyledModal = styled(Modal)`
  .teko-modal-body {
    height: 100%;
  }
`;
const StyledProductList = styled(ProductList)`
  height: 100%;
`;
const StyledProductGrid = styled(ProductGrid)`
  height: 100%;
`;
const StyledHamperGrid = styled(HamperGrid)`
  height: 100%;
`;
